/* Scrolling a list while one of its rows is dragged (owner's R31, 2026-09-03, and again on
   2026-09-29: "when we have too many cities and we need to scroll at the same time while dragging,
   it's not correctly dragged ... If we detect that we are dragging towards the edges, we need to
   start scrolling automatically").

   framer-motion's Reorder moves the row, never the scroll container, and it has no idea the
   container can scroll. Three things were wrong with the first answer, a nudge from each row's
   `onDrag`, and each needs its own piece here:

   1. **It only scrolled while the pointer moved.** `onDrag` fires on pointer movement, so holding
      a row at the bottom edge — the natural thing to do — scrolled nothing. `startEdgeAutoScroll`
      runs its own frame loop for as long as the drag lasts, faster the deeper into the band.
   2. **The row left the pointer behind.** A drag is the pointer's offset in *page* coordinates,
      and scrolling a box inside the page does not move the pointer on the page, so the row stayed
      put on screen while the list slid under it — and every slot it was compared against slid
      with the list. `contentPoint` is handed to `MotionConfig`'s `transformPagePoint`: every
      point the drag reads gets the container's scroll added, so an offset is measured in the
      list's own coordinates and a scroll is movement like any other. (The group also carries
      `layoutScroll`, which is framer's own half of the same thing: the slots are measured with
      the scroll taken off, so they are in those coordinates too.)
   3. **Nothing re-read the pointer after a scroll.** The drag only recomputes on a pointer event,
      so a scroll by this loop or by the wheel is followed by a synthetic `pointermove` at the
      pointer's last position — the row catches up with the list, and the reorder check runs.

   4. **It scrolled past the end of the list** (owner, 2026-09-29). The dragged row is moved with a
      transform, and a transform grows the container's scrollable overflow: dragging past the last
      row made the list taller, the loop scrolled into the new room, and the row went further
      still. The furthest scroll is taken ONCE, when the drag starts, and neither the loop nor the
      wheel goes past it.

   Mouse and touch alike: everything here is pointer events, which is what framer's drag listens
   to on both. The phone shell lifts a row by a resting finger (`utils/longPressDrag`) and drags
   it the same way from there; the compact shell's tablet drags at once, with a finger. */

export const EDGE_PX = 48
export const MAX_STEP_PX = 16

/** How far to scroll this frame, in pixels: negative up, positive down, 0 outside the bands.
 *  Proportional to how deep into the band the pointer is, and full speed past the edge. The band
 *  is at most a third of the box, so on a short list the two never overlap. Pure. */
export const edgeScrollStep = (rect, clientY, { edge = EDGE_PX, maxStep = MAX_STEP_PX } = {}) => {
  if (!rect || !Number.isFinite(clientY) || !(rect.height > 0)) return 0
  const band = Math.min(edge, rect.height / 3)
  const intoTop = rect.top + band - clientY
  if (intoTop > 0) return -Math.ceil(maxStep * Math.min(1, intoTop / band))
  const intoBottom = clientY - (rect.bottom - band)
  if (intoBottom > 0) return Math.ceil(maxStep * Math.min(1, intoBottom / band))
  return 0
}

/** A `transformPagePoint` that measures a drag in the scrolled container's own coordinates. Reads
 *  the ref at call time, so it can be made once and outlive the element it names. */
export const contentPoint = (containerRef) => (point) => ({
  ...point,
  y: point.y + (containerRef.current?.scrollTop ?? 0),
})

const pointerMoveAt = ({ clientX, clientY, pointerId, pointerType }) => {
  const init = { clientX, clientY, bubbles: true, cancelable: true }
  if (typeof window.PointerEvent === "function") {
    return new window.PointerEvent("pointermove", { ...init, pointerId, pointerType, isPrimary: true })
  }
  // jsdom and very old engines: framer reads a pointer with no `pointerType` as primary.
  return new window.MouseEvent("pointermove", init)
}

/** Starts scrolling `container` whenever the pointer is in one of its edge bands, until the
 *  returned stop function is called. `startEvent` is the event the drag began with, for the
 *  pointer's first position. */
export const startEdgeAutoScroll = (container, startEvent) => {
  if (!container) return () => {}
  const pointer = {
    clientX: startEvent?.clientX ?? 0,
    clientY: startEvent?.clientY,
    pointerId: startEvent?.pointerId ?? 1,
    pointerType: startEvent?.pointerType || "mouse",
  }
  let replaying = false
  let frame = null
  // The list's own length, before the dragged row's transform can add to it.
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight)

  const onPointerMove = (event) => {
    if (replaying) return
    pointer.clientX = event.clientX
    pointer.clientY = event.clientY
    if (event.pointerId != null) pointer.pointerId = event.pointerId
    if (event.pointerType) pointer.pointerType = event.pointerType
  }

  // Any scroll — this loop's or the wheel's — is a move the drag has not heard about.
  const onScroll = () => {
    if (container.scrollTop > maxTop) {
      container.scrollTop = maxTop
      return
    }
    if (!Number.isFinite(pointer.clientY)) return
    replaying = true
    try {
      window.dispatchEvent(pointerMoveAt(pointer))
    } finally {
      replaying = false
    }
  }

  const tick = () => {
    const step = edgeScrollStep(container.getBoundingClientRect(), pointer.clientY)
    if (step) {
      const next = Math.min(maxTop, Math.max(0, container.scrollTop + step))
      if (next !== container.scrollTop) container.scrollTop = next
    }
    frame = window.requestAnimationFrame(tick)
  }

  window.addEventListener("pointermove", onPointerMove, true)
  container.addEventListener("scroll", onScroll, { passive: true })
  frame = window.requestAnimationFrame(tick)

  return () => {
    window.removeEventListener("pointermove", onPointerMove, true)
    container.removeEventListener("scroll", onScroll)
    if (frame != null) window.cancelAnimationFrame(frame)
    frame = null
  }
}
